/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, forwardRef, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MapsService } from '@irian-ro/util-maps';
import { FloatLabel } from 'primeng/floatlabel';
import { NgIf } from '@angular/common';
import { LocationMapper } from '../utils/location-mapper';
import { LocationDtoModel } from '@well/delivery-libs/location-management/domain';
import { BsAutocompleteComponent, BsFloatLabelCompatDirective } from '@well/iriro-frontend-libs/bs-component-store';

@Component({
  selector: 'well-place-autocomplete',
  standalone: true,
  templateUrl: './place-autocomplete.component.html',
  styleUrls: ['./place-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaceAutocompleteComponent),
      multi: true,
    }
  ],
  imports: [FloatLabel,
    BsAutocompleteComponent, BsFloatLabelCompatDirective, FormsModule, NgIf],
})
export class PlaceAutocompleteComponent implements ControlValueAccessor {
  label= input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);

  placeChanged = output<google.maps.places.PlaceResult>();
  nearbyCities = output<google.maps.places.PlaceResult[]>();

  value = signal<{ description: string }>({ description: '' });
  predictions = signal<google.maps.places.AutocompletePrediction[]>([]);

  zoom = 8;
  location = {
    longitude: 21.228807879589795,
    latitude: 45.756349059299936,
  };

  private _onChange: any | undefined = undefined;
  private service: google.maps.places.AutocompleteService | undefined = undefined;
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined = undefined;
  private placeService: google.maps.places.PlacesService | undefined = undefined;
  map = toSignal(
    inject(MapsService)
      .load()
      .pipe(
        tap((map: boolean) => {
          if (map) {
            this.service = new google.maps.places.AutocompleteService();
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
            const div = document.createElement('div');
            this.placeService = new google.maps.places.PlacesService(div);
          }
        })
      )
  );

  writeValue(location: {description: string}): void {
    if (location) {
      this.value.set(location);
    } else {
      this.value.set({ description: '' });
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void { return; }

  onAddressInput(query: string) {
    if (query) {
      this.service?.getPlacePredictions(
        {
          input: query,
          sessionToken: this.sessionToken,
        },
        (predictions) => {
          this.predictions.set([...(predictions ?? [])]);
        }
      );
    }
  }

  getDisplayLabel = (item: { description: string } | google.maps.places.AutocompletePrediction | null): string => {
    return item?.description ?? '';
  };

  compareByDescription = (a: any | null, b: any | null): boolean => {
    return a?.description === b?.description;
  };

  changePlace(place: google.maps.places.AutocompletePrediction) {
    if (place && place.place_id) {
      this.placeService?.getDetails(
        {
          placeId: place.place_id,
          sessionToken: this.sessionToken,
        },
        (placeResult: google.maps.places.PlaceResult, status) => {
          if (placeResult.geometry === undefined || placeResult.geometry === null) {
            return;
          }
          if (this._onChange) {
            this.placeService?.nearbySearch(
              {
                location: placeResult.geometry.location,
                type: 'locality',
                radius: 1000000,
              },
              (result) => {
                if(result) {
                  this.nearbyCities.emit(result);
                }
              }
            );

            const location: LocationDtoModel = LocationMapper.mapPlaceToLocation(placeResult);
            this._onChange(location);
            this.value.set({description: placeResult.formatted_address ?? ''});
            this.placeChanged.emit(placeResult);
            this.predictions.set([]);
          }
        }
      );
    }
  }

  onPlaceClick(place: any) {
    if (place) {
      this.changePlace(place);
    }
  }
}
